import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent, type KeyboardEvent, type MouseEvent } from 'react'
import { motion, type MotionProps } from 'framer-motion'
import { kycApi } from '../../api/endpoints'
import { ApiError } from '../../api/types'
import { PressScale } from '../../lib/PressScale'
import { feedback } from '../../lib/feedback'
import { AnimatedNumber } from '../../lib/AnimatedNumber'
import { docSetFor, OTHER_TILE, spokenName, tileFor, type DocTile } from './docSets'
import { downscaleImage } from './imageDownscale'

/**
 * THE DOCKET — the company-documents step.
 *
 * ═══ THE DEAD END THIS REPLACES ═══════════════════════════════════════════
 * `SignupWizard.tsx` refused to finish unless `state.docs.length` was
 * non-zero:
 *
 *     case 'docs': if (!state.docs.length) { setError('Upload at least …'); return }
 *
 * A supplier at 11pm on a phone, whose GST certificate is a piece of paper in
 * a filing cabinet, had exactly three options: abandon, upload a wrong file,
 * or go and find a scanner — on the LAST step of a nine-step wizard, AFTER
 * the Firebase account already existed. Every abandonment there left a
 * half-provisioned account behind.
 *
 * It was purely client-side. `finish()` already guards its own
 * `kycApi.submit` call on `state.docs.length`, so a ZERO-DOCUMENT FINISH IS
 * ALREADY A SUPPORTED SERVER STATE: `kycStatus` stays `not_submitted`, the
 * user browses with locked prices, and `/kyc` is the standing resubmit
 * surface. So the block is gone, the cost is stated honestly on this screen
 * AND on the done screen, and the way back is a real link.
 *
 * ═══ THE 10x MOVE ═════════════════════════════════════════════════════════
 * The step stops assuming the documents are already DIGITAL. Every other
 * design for this screen argues about how a file already on a disk gets into
 * a box. The real population is a trader standing three feet from a filing
 * cabinet, holding a phone. One input attribute — `capture="environment"`,
 * rendered only on `(pointer: coarse)` — turns "I'll do this later" (a day of
 * delay, a review round trip, a follow-up email, an abandoned signup) into
 * eight seconds. It ships with `imageDownscale.ts` because a 12 MP photo of
 * an A4 page blows the 10 MB cap on its first real use.
 *
 * ═══ VOCABULARY ═══════════════════════════════════════════════════════════
 * A landed file gets the HOLLOW ring (`.order-node--upcoming`) — RECEIVED.
 * It never gets the struck `.cert-seal`, which in this product means
 * APPROVED. In a marketplace whose entire gate is `kycApproved`, an
 * approval-shaped mark at upload time is a lie the user would act on.
 *
 * Shared verbatim by desktop `/signup` and the mobile `/register` shell.
 * Zero backend changes: every `docType` here is already in the deployed
 * closed enum, and `POST /admin/kyc/doc-url` already allows both the `kyc/`
 * and `orgs/` prefixes.
 */

export interface UploadedDoc {
  docType: string
  objectPath: string
  fileName: string
  sizeBytes: number
}

/** A file that has left the Needed column but has not landed on the server. */
interface PendingDoc { fileName: string; sizeBytes: number }

type Entrance = Pick<MotionProps, 'initial' | 'animate' | 'transition'>

const MAX_BYTES = 10 * 1024 * 1024
const ACCEPT = '.pdf,.jpg,.jpeg,.png,.heic,.heif,application/pdf,image/*'
const ACCEPT_EXT = /\.(pdf|jpe?g|png|heic|heif)$/i

function humanSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/** HEIC often arrives with an empty MIME type, so the extension is a real gate. */
function typeAccepted(file: File): boolean {
  const t = (file.type || '').toLowerCase()
  if (t === 'application/pdf' || t.startsWith('image/')) return true
  return ACCEPT_EXT.test(file.name || '')
}

/**
 * Touch, not "small screen". `capture` is meaningless on a desktop pointer
 * (browsers ignore it) and a second button there is pure noise — and a
 * drag-and-drop layer on a phone is a trap, not a feature. One query decides
 * both halves.
 */
function useCoarsePointer(): boolean {
  const read = () => {
    try { return typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches } catch { return false }
  }
  const [coarse, setCoarse] = useState(read)
  useEffect(() => {
    if (typeof matchMedia !== 'function') return
    let mq: MediaQueryList
    try { mq = matchMedia('(pointer: coarse)') } catch { return }
    const on = () => setCoarse(mq.matches)
    // Safari < 14 has no addEventListener on MediaQueryList; there is nothing
    // to subscribe to there, and the initial read already answered.
    if (typeof mq.addEventListener !== 'function') return
    mq.addEventListener('change', on)
    return () => mq.removeEventListener('change', on)
  }, [])
  return coarse
}

function useOnline(): boolean {
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine !== false))
  useEffect(() => {
    if (typeof window === 'undefined') return
    const up = () => setOnline(true)
    const down = () => setOnline(false)
    window.addEventListener('online', up)
    window.addEventListener('offline', down)
    return () => { window.removeEventListener('online', up); window.removeEventListener('offline', down) }
  }, [])
  return online
}

// ─── Glyphs (stroke 1.5–1.6, the app vocabulary; no new assets) ─────────────

function IconPaper({ size = 22 }: { size?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M6 3h8l4 4v14H6z" className="stroke-current" />
      <path d="M14 3v4h4" className="stroke-current opacity-60" />
      <path d="M9 12h6M9 15.5h6M9 8.5h2" className="stroke-current opacity-45" />
    </svg>
  )
}

function IconCheck({ size = 13 }: { size?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M6 12.5l4 4 8-9" className="stroke-current" />
    </svg>
  )
}

function IconCamera({ size = 16 }: { size?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M3 8.5h3.5L8 6h8l1.5 2.5H21v11H3z" className="stroke-current" />
      <circle cx="12" cy="14" r="3.4" className="stroke-current" />
    </svg>
  )
}

// ─── The step ───────────────────────────────────────────────────────────────

export function StepDocs({
  docs, country, busy, reduce, onDocs, setTopError, joinPendingOrgName, onLater, onEnter,
}: {
  docs: UploadedDoc[]
  /** ISO-2 from the address step. Missing ⇒ the DEFAULT set, never an empty one. */
  country?: string | null
  busy: boolean
  reduce: boolean
  /** Functional update — two uploads can resolve in either order. */
  onDocs: (update: (prev: UploadedDoc[]) => UploadedDoc[]) => void
  setTopError: (message: string | null) => void
  /** Set only while a join request is pending: they may inherit verification. */
  joinPendingOrgName?: string | null
  /** Finish (or continue to the stock step) with whatever is filed — including nothing. */
  onLater: () => void
  /** Enter advances, matching every other step's contract. */
  onEnter: () => void
}) {
  const coarse = useCoarsePointer()
  const online = useOnline()
  const tiles = useMemo(() => docSetFor(country), [country])
  const [showOther, setShowOther] = useState(() => docs.some((d) => d.docType === OTHER_TILE.docType))
  const [pending, setPending] = useState<Record<string, PendingDoc>>({})
  const [rowError, setRowError] = useState<Record<string, string>>({})
  const [announce, setAnnounce] = useState('')
  const [dropping, setDropping] = useState(false)
  const [dropRow, setDropRow] = useState<string | null>(null)
  /**
   * The picked File, kept per docType so RETRY re-sends without making the
   * user find the file — or re-photograph the certificate — a second time.
   * A ref, not state: it must not re-render anything, and it must never reach
   * the wizard's sessionStorage.
   */
  const filesRef = useRef<Map<string, File>>(new Map())
  const dragDepth = useRef(0)
  /**
   * A 40px preview of an IMAGE that was just filed — free, via
   * `URL.createObjectURL`, no dependency and no decode work.
   *
   * Deliberately images only. A PDF thumbnail would cost ~350KB of pdf.js on
   * the conversion path to render an 80px grey rectangle, which does not
   * answer "did a readable file arrive?" — it only implies it. But a photo IS
   * the majority once the camera path exists, and 40px is exactly enough to
   * answer the real camera failure: did I photograph the certificate, or my
   * thumb. A PDF keeps the honest `filename · size` strip.
   */
  const [previews, setPreviews] = useState<Record<string, string>>({})
  const previewsRef = useRef(previews)
  previewsRef.current = previews
  // Object URLs are a document-lifetime leak until revoked; the wizard can be
  // left open for a long time on this step.
  useEffect(() => () => {
    for (const url of Object.values(previewsRef.current)) {
      try { URL.revokeObjectURL(url) } catch { /* already gone */ }
    }
  }, [])
  /** After first paint, a newly-mounted row must not replay the entrance —
   *  otherwise a row that FILES itself would play two animations at once. */
  const [mounted, setMounted] = useState(false)
  useEffect(() => { setMounted(true) }, [])

  const visibleTiles = useMemo<DocTile[]>(
    () => (showOther ? [...tiles, OTHER_TILE] : tiles),
    [tiles, showOther],
  )

  const filedTypes = useMemo(
    () => new Set([...docs.map((d) => d.docType), ...Object.keys(pending)]),
    [docs, pending],
  )
  const needed = useMemo(() => visibleTiles.filter((t) => !filedTypes.has(t.docType)), [visibleTiles, filedTypes])

  /** The packet, in docket order, then anything a resumed wizard carried in. */
  const packet = useMemo(() => {
    const order = visibleTiles.map((t) => t.docType)
    const rank = (t: string) => { const i = order.indexOf(t); return i === -1 ? 99 : i }
    return [...filedTypes]
      .sort((a, b) => rank(a) - rank(b))
      .map((docType) => ({
        tile: visibleTiles.find((t) => t.docType === docType) ?? tileFor(docType),
        doc: docs.find((d) => d.docType === docType),
        sending: pending[docType],
      }))
  }, [filedTypes, visibleTiles, docs, pending])

  // The counter counts CONFIRMED documents only: a row that has visually moved
  // into the packet but is still uploading has not been received yet, and a
  // count that runs ahead of the truth is how "it said it was done" happens.
  const requiredLeft = visibleTiles.filter((t) => t.required && !docs.some((d) => d.docType === t.docType)).length
  const optionalLeft = visibleTiles.filter((t) => !t.required && !docs.some((d) => d.docType === t.docType)).length
  const requiredFiled = requiredLeft === 0

  const clearRowError = useCallback((docType: string) => {
    setRowError((e) => {
      if (!(docType in e)) return e
      const next = { ...e }; delete next[docType]; return next
    })
  }, [])

  const dropPending = useCallback((docType: string) => {
    setPending((p) => {
      if (!(docType in p)) return p
      const next = { ...p }; delete next[docType]; return next
    })
  }, [])

  /**
   * Take a file for a row. Optimistic: the row files itself into the packet
   * immediately (the `layoutId` spring does the travel) and RETURNS to Needed
   * if anything goes wrong — the same spring, played backwards.
   */
  const accept = useCallback(async (docType: string, file: File | null | undefined) => {
    if (!file) return
    const tile = tileFor(docType)
    clearRowError(docType)
    setTopError(null)

    if (!typeAccepted(file)) {
      setRowError((e) => ({ ...e, [docType]: 'Diaex takes PDF, JPG, PNG or HEIC.' }))
      feedback.deny()
      return
    }

    filesRef.current.set(docType, file)
    setPending((p) => ({ ...p, [docType]: { fileName: file.name, sizeBytes: file.size } }))

    // A phone photo of an A4 certificate routinely exceeds the cap. Shrink it
    // before rejecting what may be the user's only copy of the document.
    const prepared = await downscaleImage(file)
    if (prepared !== file) {
      setPending((p) => (docType in p ? { ...p, [docType]: { fileName: prepared.name, sizeBytes: prepared.size } } : p))
    }

    if (prepared.size > MAX_BYTES) {
      dropPending(docType)
      setRowError((e) => ({
        ...e,
        [docType]: `That file is ${humanSize(prepared.size)} — Diaex accepts up to 10 MB. Export it smaller${coarse ? ', or photograph the page instead' : ''}.`,
      }))
      feedback.deny()
      return
    }

    try {
      const doc = await kycApi.uploadFile(docType, prepared)
      onDocs((prev) => [...prev.filter((d) => d.docType !== docType), doc])
      dropPending(docType)
      if (prepared.type.startsWith('image/') && typeof URL?.createObjectURL === 'function') {
        try {
          const url = URL.createObjectURL(prepared)
          setPreviews((p) => {
            if (p[docType]) { try { URL.revokeObjectURL(p[docType]) } catch { /* already gone */ } }
            return { ...p, [docType]: url }
          })
        } catch { /* a preview is a bonus; never let it cost the upload */ }
      }
      feedback.success()
      setAnnounce(`${spokenName(tile)} filed.`)
    } catch (err) {
      dropPending(docType)
      setRowError((e) => ({
        ...e,
        [docType]: err instanceof ApiError
          ? err.message
          : (typeof navigator !== 'undefined' && navigator.onLine === false)
            ? 'Upload failed — your connection dropped.'
            : 'Upload failed — check your connection and try again.',
      }))
      feedback.deny()
      setAnnounce(`${spokenName(tile)} did not upload.`)
    }
  }, [clearRowError, setTopError, dropPending, onDocs, coarse])

  const retry = useCallback((docType: string) => {
    const file = filesRef.current.get(docType)
    // No cached file (a reload between the failure and the retry) ⇒ clear the
    // error rather than pretend; the row is immediately pickable again.
    if (!file) { clearRowError(docType); return }
    void accept(docType, file)
  }, [accept, clearRowError])

  // ── Desktop drag-and-drop ────────────────────────────────────────────────
  const dropTarget = (dropRow && needed.some((t) => t.docType === dropRow)) ? dropRow : needed[0]?.docType ?? null
  const hasFiles = (e: DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes('Files')
  const dragProps = coarse ? {} : {
    onDragEnter: (e: DragEvent) => {
      if (!hasFiles(e)) return
      dragDepth.current += 1
      setDropping(true)
    },
    onDragOver: (e: DragEvent) => {
      if (!hasFiles(e)) return
      e.preventDefault()
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy'
    },
    onDragLeave: () => {
      dragDepth.current = Math.max(0, dragDepth.current - 1)
      if (dragDepth.current === 0) { setDropping(false); setDropRow(null) }
    },
    onDrop: (e: DragEvent) => {
      e.preventDefault()
      dragDepth.current = 0
      setDropping(false)
      const target = dropTarget
      setDropRow(null)
      if (!target) return
      void accept(target, e.dataTransfer?.files?.[0])
    },
  }

  const rowDragProps = (docType: string) => (coarse ? {} : {
    onDragOver: (e: DragEvent) => {
      if (!hasFiles(e)) return
      e.preventDefault()
      setDropRow(docType)
    },
    onDragLeave: () => setDropRow((d) => (d === docType ? null : d)),
  })

  // Enter advances, as on every other step — but never while focus is on a
  // control with its own Enter behaviour (a file input opens its picker).
  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key !== 'Enter' || e.shiftKey || e.metaKey || e.ctrlKey) return
    const el = e.target as HTMLElement | null
    if (el?.closest?.('input, button, a, label, select, textarea')) return
    e.preventDefault()
    onEnter()
  }

  const entrance = (index: number): Entrance => ({
    initial: mounted ? false : (reduce ? { opacity: 0 } : { opacity: 0, y: 10 }),
    animate: { opacity: 1, y: 0 },
    transition: {
      duration: reduce ? 0.15 : 0.5,
      delay: mounted ? 0 : Math.min(index, 6) * 0.06,
      ease: [0.16, 1, 0.3, 1],
    },
  })

  const layoutSpring: MotionProps['transition'] = reduce
    ? { duration: 0 }
    : { type: 'spring', stiffness: 340, damping: 34 }

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div
      data-testid="signup-docs"
      data-dropping={dropping ? 'true' : undefined}
      onKeyDown={onKeyDown}
      {...dragProps}
    >
      <p
        className="text-[11px] font-semibold uppercase tracking-[0.14em] text-navy-300"
        aria-label={`${requiredLeft} required and ${optionalLeft} optional documents still to add`}
        data-testid="signup-docs-counter"
      >
        <AnimatedNumber value={requiredLeft} reserve="0" duration={220} /> required
        {' · '}
        <AnimatedNumber value={optionalLeft} reserve="0" duration={220} /> optional
      </p>

      <h1 className="mt-2 font-serif text-[clamp(1.75rem,4vw,2.5rem)] font-bold text-ink tracking-h1 leading-tight">
        Your company's papers
      </h1>
      <p className="mt-2 text-sm text-navy-500">
        One document is enough to start — the rest can follow.
        {coarse ? ' A clear photo of the page is fine.' : ' PDF or a photo, up to 10 MB.'}
      </p>

      {!online && (
        <p
          className="mt-4 rounded-md border border-line bg-gold-050/60 px-4 py-3 text-sm text-navy-500"
          role="status"
          data-testid="signup-docs-offline"
        >
          You're offline. You can finish now and add documents any time.
        </p>
      )}

      <div className="mt-8 md:grid md:grid-cols-2 md:gap-8">
        {/* ── NEEDED ─────────────────────────────────────────────────────── */}
        <section aria-labelledby="docket-needed-label">
          <p id="docket-needed-label" className="text-[11px] font-semibold uppercase tracking-[0.14em] text-navy-300">
            Needed
          </p>
          <div className="rule-accent mt-1.5 mb-3" />

          {needed.length === 0 ? (
            <p className="text-sm text-navy-500" data-testid="signup-docs-needed-empty">
              Everything we asked for is in the packet. You can add more any time from your account.
            </p>
          ) : (
            <div className="space-y-3">
              {needed.map((tile, i) => (
                <DocRow
                  key={tile.docType}
                  tile={tile}
                  variant="needed"
                  coarse={coarse}
                  busy={busy}
                  litEdge={dropping && dropTarget === tile.docType}
                  error={rowError[tile.docType]}
                  entrance={entrance(i)}
                  layoutSpring={layoutSpring}
                  reduce={reduce}
                  dragProps={rowDragProps(tile.docType)}
                  onFile={(f) => void accept(tile.docType, f)}
                  onRetry={() => retry(tile.docType)}
                  onDismissError={() => clearRowError(tile.docType)}
                />
              ))}
            </div>
          )}

          {!showOther && (
            <PressScale
              feel="tap"
              onClick={() => setShowOther(true)}
              className="btn-ghost mt-3 h-11 px-0 text-sm underline underline-offset-4"
              data-testid="signup-docs-other-reveal"
            >
              My papers aren't listed →
            </PressScale>
          )}
        </section>

        {/* ── GOING TO REVIEW ────────────────────────────────────────────── */}
        <section aria-labelledby="docket-packet-label" className="mt-8 md:mt-0">
          <p id="docket-packet-label" className="text-[11px] font-semibold uppercase tracking-[0.14em] text-navy-300">
            Going to review
          </p>
          <div className="rule-accent mt-1.5 mb-3" />

          <div className="min-h-[76px]" data-testid="signup-docs-filed">
            {packet.length === 0 ? (
              <p className="text-sm text-navy-500">Nothing filed yet — one document is enough to start.</p>
            ) : (
              <div className="space-y-3" role="list">
                {packet.map((row, i) => (
                  <DocRow
                    key={row.tile.docType}
                    tile={row.tile}
                    variant="filed"
                    coarse={coarse}
                    busy={busy}
                    doc={row.doc}
                    sending={row.sending}
                    preview={previews[row.tile.docType]}
                    entrance={entrance(i)}
                    layoutSpring={layoutSpring}
                    reduce={reduce}
                    onFile={(f) => void accept(row.tile.docType, f)}
                  />
                ))}
              </div>
            )}
          </div>

          <p className="mt-4 flex items-center gap-2 text-xs text-navy-500">
            <span className="h-3 w-3 shrink-0 rounded-full order-node--upcoming" aria-hidden />
            Diaex verification · usually 1–2 business days
          </p>
        </section>
      </div>

      {/* The join-pending exit keeps its own explanation: someone whose company
          can verify them for FREE must not be told to go and scan an
          incorporation certificate. */}
      {joinPendingOrgName && (
        <div className="mt-8 rounded-md border border-line bg-gold-050/60 p-4">
          <p className="text-sm leading-relaxed text-navy-500">
            Waiting on <span className="font-semibold text-ink">{joinPendingOrgName}</span>?
            {' '}You can skip these — if they approve you, you inherit their verification.
            If they don't, we'll ask again.
          </p>
          <PressScale
            feel="tap"
            disabled={busy}
            onClick={onLater}
            className="btn-secondary mt-3"
            data-testid="signup-docs-skip-join"
          >
            Skip for now
          </PressScale>
        </div>
      )}

      {/* ═══ THE EXIT ═══════════════════════════════════════════════════════
          Rendered while the one required document is still missing — i.e.
          exactly when the old code raised an error and refused to move.
          Never disabled, a real 44px control, and it states the cost instead
          of hiding it. Once that document IS filed the primary Finish button
          is unambiguous and this would be noise, so it retires. */}
      {!requiredFiled && (
        <div className="mt-8 border-t border-line pt-5" data-testid="signup-docs-later-block">
          <p className="text-sm text-navy-500">
            Haven't got them to hand? You can browse the catalog straight away — prices and
            supplier contact unlock once we've verified you, and you can add these any time.
          </p>
          <PressScale
            feel="tap"
            disabled={busy}
            onClick={onLater}
            className={`mt-3 h-11 px-4 text-sm underline underline-offset-4 ${online ? 'btn-ghost' : 'btn-secondary'}`}
            data-testid="signup-docs-later"
          >
            I'll add these later →
          </PressScale>
        </div>
      )}

      {/* ONE deliberate live region. The counter is not live as well, or every
          upload would announce twice. */}
      <p className="sr-only" aria-live="polite" aria-atomic="true">
        {announce
          ? `${announce} ${docs.length} document${docs.length === 1 ? '' : 's'} ready to send.`
          : ''}
      </p>
    </div>
  )
}

// ─── One row, two states ────────────────────────────────────────────────────

/**
 * `needed` and `filed` are the SAME component under one `layoutId`, which is
 * what makes the filing motion free: framer FLIPs the real box out of the
 * Needed column and into the packet, and a failed upload plays the identical
 * spring in reverse.
 *
 * The `data-testid` travels with the row in BOTH states, because
 * `scripts/qa/signup-live-e2e.mjs` asserts a descendant `input[type=file]`
 * and — once uploaded — a descendant `.text-success` under
 * `[data-testid="signup-doc-business_license"]`.
 */
function DocRow({
  tile, variant, coarse, busy, doc, sending, preview, error, litEdge, entrance, layoutSpring, reduce,
  dragProps, onFile, onRetry, onDismissError,
}: {
  tile: DocTile
  variant: 'needed' | 'filed'
  coarse: boolean
  busy: boolean
  doc?: UploadedDoc
  sending?: PendingDoc
  /** Object-URL preview of a just-filed IMAGE. Absent for PDFs and for a
   *  resumed wizard, which holds no File to preview. */
  preview?: string
  error?: string
  litEdge?: boolean
  entrance: Entrance
  layoutSpring: MotionProps['transition']
  reduce: boolean
  dragProps?: Record<string, unknown>
  onFile: (file: File | null | undefined) => void
  onRetry?: () => void
  onDismissError?: () => void
}) {
  const filed = variant === 'filed'
  const landed = filed && !!doc
  const hintId = `docket-hint-${tile.docType}`
  const statusId = `docket-status-${tile.docType}`
  const name = spokenName(tile)
  const status = landed
    ? `filed, ${doc!.fileName}`
    : sending ? 'sending'
      : error ? `not added. ${error}`
        : tile.required ? 'required, not yet added'
          : 'optional, not yet added'

  // Clicking a <label> forwards the activation to its input, whose click then
  // bubbles back through the label — so an unguarded handler ticks twice.
  const tapOnce = (e: MouseEvent<HTMLLabelElement>) => {
    if ((e.target as HTMLElement)?.tagName !== 'INPUT') feedback.tap()
  }

  return (
    <motion.div
      layoutId={`doc:${tile.docType}`}
      layout={!reduce}
      transition={layoutSpring}
      role={filed ? 'listitem' : undefined}
      data-testid={`signup-doc-${tile.docType}`}
      className={[
        'docket-row relative rounded-md border bg-paper px-4 py-3.5 transition-colors duration-default ease-premium',
        landed ? 'docket-received' : '',
        litEdge ? 'lqd-edge' : '',
        // A champagne rim, never red, never an asterisk.
        !filed && tile.required ? 'border-gold-500/45 bg-gold-050/40' : 'border-line',
      ].filter(Boolean).join(' ')}
      {...dragProps}
    >
      <motion.div {...entrance}>
        <div className="flex items-start gap-3">
          {landed && preview ? (
            // The photo itself, with the received ring as a badge on it. The
            // `.text-success` node stays inside the badge — signup-live-e2e
            // waits on it as a descendant of this row.
            <span className="relative mt-0.5 block h-10 w-10 shrink-0" aria-hidden>
              <img
                src={preview}
                alt=""
                className="h-10 w-10 rounded-sm border border-line object-cover"
                data-testid={`signup-doc-preview-${tile.docType}`}
              />
              <span className="absolute -bottom-1 -right-1 grid h-[18px] w-[18px] place-items-center rounded-full bg-paper order-node--upcoming">
                <span className="text-success"><IconCheck size={11} /></span>
              </span>
            </span>
          ) : (
            <span
              className={landed
                ? 'mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full order-node--upcoming'
                : 'mt-0.5 shrink-0 text-navy-300'}
              aria-hidden
            >
              {/* RECEIVED, not approved — a hollow ring with a quiet check,
                  never the struck .cert-seal. */}
              {landed ? <span className="text-success"><IconCheck /></span> : <IconPaper />}
            </span>
          )}

          <div className="min-w-0 flex-1">
            <div className="flex items-baseline justify-between gap-3">
              <p className="min-w-0 text-sm font-semibold leading-snug text-ink">{tile.title}</p>
              {/* A quiet label in the register of the step counter — a
                  champagne word, never a red asterisk. */}
              {!filed && (
                <span
                  className={`shrink-0 text-[10px] font-semibold uppercase tracking-[0.12em] ${
                    tile.required ? 'text-gold-600' : 'text-navy-300'
                  }`}
                >
                  {tile.required ? 'required' : 'helps'}
                </span>
              )}
            </div>
            <div id={hintId} className="mt-0.5 text-xs leading-relaxed text-navy-500">
              {landed ? (
                <span className="block truncate" title={doc!.fileName}>
                  {doc!.fileName}{humanSize(doc!.sizeBytes) ? ` · ${humanSize(doc!.sizeBytes)}` : ''}
                </span>
              ) : sending ? (
                <span className="block truncate" title={sending.fileName}>{sending.fileName} · Sending…</span>
              ) : (
                tile.hint
              )}
            </div>
            {sending && <div className="skeleton mt-2 h-1 w-full rounded-full" aria-hidden />}
            <span id={statusId} className="sr-only">{status}</span>
          </div>
        </div>

        {/* Triggers. A <label> wrapping an .sr-only input keeps the NATIVE file
            picker, native keyboard activation and native iOS behaviour — none
            of it re-implemented. `focus-within` (not `:has()`, which older
            Android WebViews miss) draws the ring that a 1px-clipped input
            cannot show: today that input takes focus with NO visible
            indication at all. */}
        <div className="mt-2.5 flex flex-wrap items-center gap-2">
          {coarse && !landed && (
            <label
              className="btn-secondary h-11 cursor-pointer px-4 text-sm focus-within:shadow-[0_0_0_3px_var(--gold-300)]"
              onClick={tapOnce}
              data-testid={`signup-doc-capture-${tile.docType}`}
            >
              <IconCamera />
              <span>Photograph</span>
              <input
                type="file"
                accept="image/*"
                capture="environment"
                className="sr-only"
                disabled={busy}
                aria-label={`Photograph your ${name}`}
                aria-describedby={`${hintId} ${statusId}`}
                onChange={(e) => { onFile(e.target.files?.[0]); e.target.value = '' }}
              />
            </label>
          )}
          <label
            className="btn-secondary h-11 cursor-pointer px-4 text-sm focus-within:shadow-[0_0_0_3px_var(--gold-300)]"
            onClick={tapOnce}
          >
            <span>{landed ? 'Replace' : sending ? 'Choose another' : 'Choose file'}</span>
            <input
              type="file"
              accept={ACCEPT}
              className="sr-only"
              disabled={busy}
              aria-label={landed ? `Replace your ${name}` : `Add your ${name}`}
              aria-describedby={`${hintId} ${statusId}`}
              onChange={(e) => { onFile(e.target.files?.[0]); e.target.value = '' }}
            />
          </label>
        </div>

        {error && (
          <div className="mt-2" role="alert">
            <p className="text-xs text-danger" data-testid={`signup-doc-error-${tile.docType}`}>{error}</p>
            <div className="flex flex-wrap items-center gap-3">
              {onRetry && (
                <PressScale
                  feel="tap"
                  onClick={onRetry}
                  className="btn-ghost h-11 px-0 text-xs font-semibold underline underline-offset-4"
                  data-testid={`signup-doc-retry-${tile.docType}`}
                >
                  Retry
                </PressScale>
              )}
              {onDismissError && (
                <PressScale
                  feel="dim"
                  onClick={onDismissError}
                  className="btn-ghost h-11 px-0 text-xs underline underline-offset-4"
                  data-testid={`signup-doc-skip-${tile.docType}`}
                >
                  Continue without this
                </PressScale>
              )}
            </div>
          </div>
        )}
      </motion.div>
    </motion.div>
  )
}
